import { ResourceBaseService } from "@/core/common/resource-base/resource-base.service";
import { ApiHttpClient } from "@/core/common/http/api-http-client";
import { ValuationDto } from "@/core/features/valuations/valuation.dto";
import { CreateValuationDto } from "@/core/features/valuations/create-valuation.dto";
import { UpdateValuationDto } from "@/core/features/valuations/update-valuation.dto";
import { HttpRequest } from "@/core/common/http/http-request";
import { plainToInstance } from "class-transformer";
import { SetValuationResponsibleDto } from "@/core/features/valuations/set-valuation-responsible.dto";
import { SetValuationCertificationDto } from "@/core/features/valuations/set-valuation-certification.dto";
import { SetValuationStatusDto } from "@/core/features/valuations/set-valuation-status.dto";
import { AddValuationProjectsDto } from "@/core/features/valuations/add-valuation-projects.dto";
import { RemoveValuationProjectsDto } from "@/core/features/valuations/remove-valuation-projects.dto";
import { ValuationStatusEnum } from "@altertec_gparn/lib";
import { IRequest } from "@/interfaces/IRequest";
import { ExecutionReportRowDto } from "@/core/features/valuations/execution-report-row.dto";
import { ValuationTotalDto } from "@/core/features/valuations/valuation-total.dto";
import { ExecutionReportTotalDto } from "@/core/features/valuations/execution-report-total.dto";
import { FindManyResult } from "@/core/common/resource-base/find-many-result.dto";
import {
  SetExecutionReportRegistrationDateBulkDto,
  SetExecutionReportRegistrationDateBulkItemDto,
} from "@/core/features/valuations/set-execution-report-registration-date-bulk.dto";
import { SetExecutionReportRegistrationDateBulkResult } from "@/core/features/valuations/set-execution-report-registration-date-bulk-result.interface";

export class ValuationsResource extends ResourceBaseService<ValuationDto, CreateValuationDto, UpdateValuationDto> {
  protected EntityDto = ValuationDto;
  protected CreateEntityDto = CreateValuationDto;
  protected UpdateEntityDto = UpdateValuationDto;

  constructor(protected readonly apiHttpClient: ApiHttpClient) {
    super(apiHttpClient, "/valuations");
  }

  setResponsible(valuationId: string, responsibleUserId: string): Promise<ValuationDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/responsible",
        method: "PATCH",
        auth: true,
        body: plainToInstance(SetValuationResponsibleDto, { valuationId, responsibleUserId }),
      })
    );
  }

  setCertification(valuationId: string, certificationId?: string): Promise<ValuationDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/certification",
        method: "PATCH",
        auth: true,
        body: plainToInstance(SetValuationCertificationDto, { valuationId, certificationId }),
      })
    );
  }

  setStatus(valuationId: string, status: ValuationStatusEnum, registrationDate?: string | Date): Promise<ValuationDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/status",
        method: "PATCH",
        auth: true,
        body: plainToInstance(SetValuationStatusDto, { valuationId, status, registrationDate }),
      })
    );
  }

  addProjects(valuationId: string, projectIds: string[]): Promise<ValuationDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/add-projects",
        method: "PATCH",
        auth: true,
        body: plainToInstance(AddValuationProjectsDto, { valuationId, projectIds }),
      })
    );
  }

  removeProjects(valuationId: string, valuationHasProjectIds: string[]): Promise<void> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/remove-projects",
        method: "PATCH",
        auth: true,
        body: plainToInstance(RemoveValuationProjectsDto, { valuationId, valuationHasProjectIds }),
      })
    );
  }

  async getExecutionReport(request: IRequest): Promise<FindManyResult<ExecutionReportRowDto>> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/execution-report",
        method: "GET",
        auth: true,
        query: this.getQueryBuilder(request).query(),
      })
    );
    const response = result as FindManyResult<ExecutionReportRowDto>;
    return {
      ...response,
      data: plainToInstance(ExecutionReportRowDto, response.data),
    };
  }

  async getExecutionReportTotal(request: IRequest): Promise<ExecutionReportTotalDto> {
    return this.apiHttpClient
      .request(
        HttpRequest.create({
          url: this.resourceUrl + "/execution-report/total",
          method: "GET",
          auth: true,
          query: this.getQueryBuilder(request).query(),
        })
      )
      .then((result) => plainToInstance(ExecutionReportTotalDto, result));
  }

  async getTotal(request: IRequest): Promise<ValuationTotalDto> {
    return this.apiHttpClient
      .request(
        HttpRequest.create({
          url: this.resourceUrl + "/total",
          method: "GET",
          auth: true,
          query: this.getQueryBuilder(request).query(),
        })
      )
      .then((result) => plainToInstance(ValuationTotalDto, result));
  }

  async exportExecutionReportByRegistrationDateExcel(request: IRequest): Promise<{ status: "queued"; jobId: string }> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/execution-report/by-registration-date/excel/export",
        method: "GET",
        auth: true,
        query: this.getQueryBuilder({ ...request, limit: 0, page: 1 }).query(),
      })
    );
  }

  async exportBillingSummaryExcel(request: IRequest): Promise<{ status: "queued"; jobId: string }> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/execution-report/billing-summary/excel/export",
        method: "GET",
        auth: true,
        query: this.getQueryBuilder({ ...request, limit: 0, page: 1 }).query(),
      })
    );
  }

  async setExecutionReportRegistrationDateInBulk(
    items: SetExecutionReportRegistrationDateBulkItemDto[],
    registrationDate: string | Date
  ): Promise<SetExecutionReportRegistrationDateBulkResult> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/execution-report/bulk/registration-date",
        method: "PATCH",
        auth: true,
        body: plainToInstance(SetExecutionReportRegistrationDateBulkDto, {
          items,
          registrationDate,
        }),
      })
    );
  }
}
